import moment, { Moment, MomentInput } from 'moment-timezone';
import { IntlDateFormatName, IntlDateFormatter } from '@blocs.i18n';
import { gqlTimeZone } from '@minecraft.graphql-types';
import dateFormat from './constants';

type TZFormat =
  | gqlTimeZone
  | {
      abbreviation?: string;
      standardName?: string;
    };

export function addTimeZone(date = '', tzAbbreviation?: string): string {
  if (!tzAbbreviation) return date;

  return `${date} ${tzAbbreviation}`;
}

export function getLocalTimeZone(): TZFormat {
  const standardName = moment.tz.guess();

  return { standardName, abbreviation: moment.tz(standardName).zoneName() };
}

export function getDate(date: MomentInput, format?: dateFormat, tz?: TZFormat): string | null {
  if (!date) return null;

  return addTimeZone(moment.parseZone(date).format(format), tz?.abbreviation);
}

export function getDateWithTimeZone(
  date: MomentInput,
  format: dateFormat,
  tz: TZFormat,
  skipTzAbbreviation = false
): string | null {
  if (!date) return null;

  const returnedDate = moment.tz(date, tz.standardName).format(format);

  return skipTzAbbreviation ? returnedDate : addTimeZone(returnedDate, tz?.abbreviation);
}

export function getDateWithLocalTimeZone(
  date: MomentInput,
  format: dateFormat,
  skipTzAbbreviation = false
): string | null {
  return getDateWithTimeZone(date, format, getLocalTimeZone(), skipTzAbbreviation);
}

export function convertDateFromTzToTz(
  date: MomentInput,
  format: dateFormat,
  sourceTz: TZFormat,
  destTz: TZFormat,
  skipTzAbbreviation = false
): string | null {
  if (!sourceTz?.standardName || !destTz?.standardName) {
    // if the timezone is undefined then moment will just default
    // to UTC which will give the wrong date, better to
    // return nothing so it's easier to catch the bug
    return '';
  }

  // strip UTC "Z" timezone off and cast the date to the source timezone
  const sourceDate = moment.tz(date.toString().replace('Z', ''), sourceTz.standardName);

  // pass the date that is set to the correct local source time,
  // then pass into getDateWithTimeZone which will format it to the destination TZ
  return getDateWithTimeZone(sourceDate.toISOString(), format, destTz, skipTzAbbreviation);
}

export function convertIntlDateToTz(date: MomentInput, sourceTzStandardName: string): string | null {
  if (!sourceTzStandardName) {
    // if the timezone is undefined then moment will just default
    // to UTC which will give the wrong date, better to
    // return nothing so it's easier to catch the bug
    return '';
  }

  // strip UTC "Z" timezone off and cast the date to the source timezone
  return moment.tz(date.toString().replace('Z', ''), sourceTzStandardName).toISOString();
}

export function getIntlMultiFormatDate(
  formatIntlDate: IntlDateFormatter,
  date: string,
  formats: IntlDateFormatName[],
  timeZoneStandardName?: string,
  destTimeZoneStandardName?: string
): string[] {
  return formats.map((format) => {
    if (!date || !timeZoneStandardName || !destTimeZoneStandardName) {
      return '-';
    }

    return formatIntlDate({
      date,
      timeZoneStandardName,
      destTimeZoneStandardName,
      format,
    });
  });
}

export function isValidDate(dateString: string) {
  if (typeof dateString !== 'string') {
    return false;
  }

  const date = moment(dateString);
  return date.isValid();
}

export const parseTimeToDateString = (timeStr: string) => {
  if (!timeStr) {
    return 'Invalid date';
  }
  const timeParts = timeStr?.split(':');
  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);

  if (minutes > 59 || hours > 23 || minutes < 0 || hours < 0) {
    return 'Invalid date';
  }
  if (timeParts?.length !== 2 || Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 'Invalid date';
  }

  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);

  return date.toUTCString();
};

/** given an ISO date string, returns another ISO date string where the time portion is set to the start of the day
 * if no date is provided, the current date is used
 * @example (assuming date is May 8, 2021) getStartOfDay() // '2021-05-08T00:00:00.000Z'
 * @example getStartOfDay('2021-08-01T12:00:00.000Z') // '2021-08-01T00:00:00.000Z'
 */
export const getStartOfDay = (isoDateString?: string): string => {
  return moment.utc(isoDateString).startOf('day').toISOString();
};

/** given an ISO date string, returns another ISO date string where the time portion is set to the end of the day
 * if no date is provided, the current date is used
 * @example (assuming date is May 8, 2021) getEndOfDay() // '2021-05-08T23:59:99.999Z'
 * @example getEndOfDay('2021-08-01T12:00:00.000Z') // '2021-08-01T23:59:99.999Z'
 */
export const getEndOfDay = (isoDateString?: string): string => {
  return moment.utc(isoDateString).endOf('day').toISOString();
};

/**
 * given an ISO date string, returns true if the date is in the past
 * @param isoDateString - the date to check
 * @param includeSame - if true, will return true if the date matches the current date, false by default
 *
 * @example
 * // current date = '2021-08-01T12:00:00.000Z'
 * isDateInPast({ isoDateString: '2021-07-31T12:00:00.000Z' }) // true
 * isDateInPast({ isoDateString: '2021-08-01T12:00:00.000Z' }) // false
 * isDateInPast({ isoDateString: '2021-08-01T12:00:00.000Z', includeSame: true }) // true
 * */
export const isDateInPast = ({
  isoDateString,
  includeSame = false,
}: {
  isoDateString: string;
  includeSame?: boolean;
}): boolean => {
  const dateMoment = moment.utc(isoDateString);
  const currentMoment = moment.utc();

  if (includeSame) {
    return dateMoment.isSameOrBefore(currentMoment);
  }

  return dateMoment.isBefore(currentMoment);
};

/**
 * given as ISO date string, returns true if the date is in the future
 * @param isoDateString - the date to check
 * @param includeSame - if true, will return true if the date matches the current date, false by default
 *
 * @example
 * // current date = '2021-08-01T12:00:00.000Z'
 * isDateInFuture('2021-08-02T12:00:00.000Z') // true
 * isDateInFuture('2021-08-01T12:00:00.000Z') // false
 * isDateInFuture('2021-08-01T12:00:00.000Z', true) // true
 * */
export const isDateInFuture = ({
  isoDateString,
  includeSame = false,
  futureCutoff = null,
}: {
  isoDateString: string;
  includeSame?: boolean;
  futureCutoff?: { count: number; unit: 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second' };
}): boolean => {
  const dateMoment = moment.utc(isoDateString);
  const currentMoment = moment.utc();
  const futureCutoffMoment = futureCutoff ? moment.utc().add(futureCutoff.count, futureCutoff.unit) : null;

  if (includeSame) {
    return (
      dateMoment.isSameOrAfter(currentMoment) &&
      (futureCutoffMoment ? dateMoment.isSameOrBefore(futureCutoffMoment) : true)
    );
  }

  return dateMoment.isAfter(currentMoment) && (futureCutoffMoment ? dateMoment.isBefore(futureCutoffMoment) : true);
};

/** given two dates in ISO format, this will return ISO strings for each date from the start date to the end date
 * @param startDate - the start date in ISO format
 * @param endDate - the end date in ISO format
 * @example
 * const startDate = '2021-08-01T12:00:00.000Z';
 * const endDate = '2021-08-04T12:00:00.000Z';
 * console.log(enumerateDaysBetweenDates(startDate, endDate));
 * // ['2021-08-01T00:00:00.000Z', '2021-08-02T00:00:00.000Z', '2021-08-03T00:00:00.000Z', '2021-08-04T00:00:00.000Z']
 */
export const enumerateDaysBetweenDates = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
  withOffset?: boolean;
}): string[] => {
  const startMoment = moment.utc(startDate).startOf('day');
  const endMoment = moment.utc(endDate).endOf('day');
  const outputIsoStrings = [];

  while (startMoment.isSameOrBefore(endMoment)) {
    outputIsoStrings.push(startMoment.startOf('day').toISOString());
    startMoment.add(1, 'days');
  }

  return outputIsoStrings;
};

export const getAgeInYears = (date: string): number => {
  if (!isValidDate(date)) {
    return undefined;
  }

  return moment.utc().diff(date, 'years');
};

/**
 * Subtracts a specified amount of time from the current date and returns the result as an ISO string.
 * @param amount - The amount of time to subtract.
 * @param unit - The unit of time to subtract.
 * @returns {string} The resulting date as an ISO string.
 *
 * @example
 * Subtract 1 day from the current date
 * const result = subtractFromDate({ amount: 1, unit: 'day' });
 * console.log(result); // Output: "2022-01-01T00:00:00.000Z"
 */
export const subtractFromDate = ({
  date,
  amount,
  unit,
}: {
  date?: Date;
  amount: number;
  unit: moment.unitOfTime.DurationConstructor;
}): string => {
  return moment(date || new Date())
    .utc()
    .subtract(amount, unit)
    .toISOString();
};

const isNil = (val: unknown) => val === undefined || val === null;

export type DateType = string | Date | moment.Moment;

export const getFormattedDate = (date: DateType, formatIntlDate: IntlDateFormatter): string => {
  let momentDate = null;

  if (isNil(date)) return momentDate;

  if (typeof date === 'string') {
    momentDate = moment.utc(date);
  }

  if (date instanceof Date) {
    momentDate = moment.utc(moment(date).utc(true).format());
  }

  if (date instanceof moment) {
    momentDate = (date as Moment).utc(true);
  }

  return formatIntlDate({ date: momentDate?.toISOString(), format: 'MONTH_DAY' });
};

export const calculateTimeLeft = (endsAt: Date) => {
  const now = new Date();
  const target = new Date(endsAt);
  const diff = Math.max(target.getTime() - now.getTime(), 0);

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return { days, hours, minutes, seconds };
};
